/** Helpers for reading signed-in user/org from localStorage (DynamoDB AttributeValue shape). */

import { describeDashboardPath } from "./navigation";

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function getStoredOrganization() {
  try {
    return JSON.parse(localStorage.getItem("organization") || "null");
  } catch {
    return null;
  }
}

export function getOrgId(org = getStoredOrganization()) {
  if (!org) return null;
  const raw = org.orgId ?? org.org_id;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

export function getUserId(user = getStoredUser()) {
  if (!user) return null;
  const raw = user.userId ?? user.user_id;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

export function getUserEmail(user = getStoredUser()) {
  if (!user) return null;
  const raw = user.email;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

export function getUserDisplayName(user = getStoredUser()) {
  if (!user) return "there";
  const first = user.fName?.S || user.first_name?.S || user.firstName?.S || "";
  const last = user.lName?.S || user.last_name?.S || user.lastName?.S || "";
  const name = `${first} ${last}`.trim();
  if (name) return name;
  return user.email?.S || "there";
}

export function getOrgName(org = getStoredOrganization()) {
  return org?.organization_name?.S || org?.name?.S || "your organization";
}

/** Resolve current user's role in the active org: owner | admin | user | null */
export function getCurrentUserRole(
  org = getStoredOrganization(),
  user = getStoredUser()
) {
  const userId = getUserId(user);
  if (!org || !userId) return null;
  const entry = org?.users?.L?.find((u) => u?.M?.userId?.S === userId);
  return entry?.M?.type?.S ?? null;
}

export function isAdminOrOwner(role = getCurrentUserRole()) {
  return role === "admin" || role === "owner";
}

export function dynAttr(value) {
  if (value == null) return null;
  if (typeof value === "object") {
    if (value.S !== undefined) return value.S;
    if (value.N !== undefined) return Number(value.N);
    if (value.BOOL !== undefined) return value.BOOL;
  }
  return value;
}

/** Stripe subscription trial (not Twilio account trial). */
export function isOrgOnTrial(org = getStoredOrganization()) {
  const status =
    org?.subscription_details?.M?.sub_status?.S ??
    dynAttr(org?.subscription_details?.sub_status);
  // Org records use "trial"; Stripe API responses use "trialing".
  return status === "trial" || status === "trialing";
}

export function isCreateRoutePath(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  return path === "/dashboard/create-route" || path === "/create-route";
}

/** Edit existing route (`/dashboard/call-route/:id`), not the legacy `-old` routes. */
export function isEditRoutePath(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  const parts = path.split("/").filter(Boolean);
  // /dashboard/call-route/:id
  if (
    parts.length === 3 &&
    parts[0] === "dashboard" &&
    parts[1] === "call-route" &&
    Boolean(parts[2]) &&
    !parts[2].endsWith("-old")
  ) {
    return true;
  }
  // Relative/unprefixed fallback
  return parts.length === 2 && parts[0] === "call-route" && Boolean(parts[1]);
}

export function buildDynamicVariables(options = {}) {
  const user = getStoredUser();
  const org = getStoredOrganization();
  const { path, page_label } = describeDashboardPath(options.pathname);
  const vars = {
    user_name: getUserDisplayName(user),
    user_email: getUserEmail(user) || "",
    user_id: getUserId(user) || "",
    org_name: getOrgName(org),
    org_id: getOrgId(org) || "",
    org_timezone: dynAttr(org?.org_timezone) || "",
    user_role: getCurrentUserRole(org, user) || "user",
    current_path: path || options.pathname || "",
    current_page: page_label,
  };
  if (options.entryIntent) {
    vars.entry_intent = options.entryIntent;
  }
  return vars;
}

/**
 * Session extras when opening the assistant from a known entry point
 * (e.g. post-onboarding What Next → inbound).
 *
 * Requires first_message override enabled on the ElevenLabs agent (Security tab).
 * Replaces the default "what would you like to do?" greeting and skips the
 * follow-up nudge so the user sees one starter — not greeting + nudge reply.
 *
 * Passes entry_intent as a dynamic variable so the agent prompt can also
 * specialize (optional dashboard change).
 */
export function buildSessionExtrasForEntryIntent(entryIntent) {
  if (entryIntent !== "post_onboarding_inbound") return null;
  const name = getUserDisplayName();
  return {
    entryIntent,
    firstMessageOverride:
      `Hi ${name} — Twilio is connected. What would you like to happen when someone calls your Twilio number? ` +
      `Describe it in your own words and I'll help you set up the inbound call routing.`,
    skipPanelOpenedNudge: true,
    skipActionableNudge: true,
  };
}





